(() => {
  const isThemeEditor = Boolean(
    (window.Shopify && window.Shopify.designMode) ||
    (window.GearRackTheme && window.GearRackTheme.designMode)
  );
  if (isThemeEditor) return;

  const PRICE_SORTS = new Set(['price-ascending', 'price-descending']);
  let latestRun = 0;
  let observer = null;
  let isRendering = false;

  function params() {
    return new URLSearchParams(window.location.search);
  }

  function currentSort() {
    return params().get('sort_by') || '';
  }

  function isPriceSort() {
    return PRICE_SORTS.has(currentSort());
  }

  function grid() {
    return document.querySelector('#product-grid[data-gr-available-price-grid="true"]');
  }

  function productKey(item) {
    const link = item.querySelector('a[href*="/products/"]');
    if (!link) return item.textContent || Math.random().toString(36);
    try {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      return url.pathname.replace(/\/collections\/[^/]+\/products\//, '/products/');
    } catch (error) {
      return link.getAttribute('href');
    }
  }

  function itemData(item, index) {
    const card = item.querySelector('product-card[data-available-min-price]');
    if (!card) return null;

    const rawPrice = card.dataset.availableMinPrice;
    const availablePrice = parseInt(rawPrice || '', 10);
    const available = card.dataset.productAvailable !== 'false';

    return {
      item,
      index,
      key: productKey(item),
      available,
      availablePrice: Number.isFinite(availablePrice) ? availablePrice : Number.MAX_SAFE_INTEGER
    };
  }

  function itemsFromDocument(doc) {
    return Array.from(doc.querySelectorAll('#product-grid > .column'))
      .map((item, index) => itemData(item, index))
      .filter(Boolean);
  }

  function itemsFromGrid(activeGrid) {
    return Array.from(activeGrid.querySelectorAll(':scope > .column'))
      .map((item, index) => itemData(item, index))
      .filter(Boolean);
  }

  function sortedItems(items) {
    const sortBy = currentSort();
    const direction = sortBy === 'price-descending' ? -1 : 1;

    return items.slice().sort((a, b) => {
      if (a.available !== b.available) return a.available ? -1 : 1;
      const priceDiff = (a.availablePrice - b.availablePrice) * direction;
      if (priceDiff !== 0) return priceDiff;
      return a.index - b.index;
    });
  }

  function sectionUrl(pageNumber, sectionId) {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('page', pageNumber);
    return url.toString();
  }

  async function fetchPageItems(pageNumber, sectionId) {
    const response = await fetch(sectionUrl(pageNumber, sectionId), { credentials: 'same-origin' });
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return itemsFromDocument(doc);
  }

  function clearGrid(activeGrid) {
    activeGrid.querySelectorAll(':scope > .column').forEach((item) => item.remove());
  }

  function originalPagination() {
    return document.querySelector('pagination-theme');
  }

  function customPagination() {
    return document.getElementById('grAvailablePricePagination');
  }

  function cleanupPagination() {
    const pagination = originalPagination();
    const custom = customPagination();
    if (pagination) pagination.style.display = '';
    if (custom) custom.remove();
  }

  function renderPagination(activeGrid, items, currentPage, paginationType) {
    const perPage = Math.max(parseInt(activeGrid.dataset.productsPerPage || '24', 10), 1);
    const totalPages = Math.ceil(items.length / perPage);
    const nativePagination = originalPagination();
    let container = customPagination();

    if (nativePagination) nativePagination.style.display = 'none';

    if (!container) {
      container = document.createElement('div');
      container.id = 'grAvailablePricePagination';
      container.className = 'gr-available-price-pagination';
      (nativePagination || activeGrid).insertAdjacentElement('afterend', container);
    }

    if (totalPages <= 1) {
      container.innerHTML = '';
      return;
    }

    if (paginationType === 'loadmore' || paginationType === 'infinite') {
      const shown = Math.min(currentPage * perPage, items.length);
      const hasMore = shown < items.length;
      container.innerHTML = hasMore ? '<div class="row"><div class="small-12 columns"><button class="load-more button" type="button"><span>Load more</span></button></div></div>' : '';
      const button = container.querySelector('.load-more');
      if (button) button.addEventListener('click', () => renderPage(activeGrid, items, currentPage + 1, true));
      return;
    }

    const parts = [];
    parts.push(`<span class="prev"><a href="#" data-gr-available-page="${Math.max(currentPage - 1, 1)}" ${currentPage === 1 ? 'aria-disabled="true"' : ''}>‹<span class="visually-hidden">Previous</span></a></span>`);

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 2) {
        parts.push(`<span class="page ${i === currentPage ? 'current' : ''}">${i === currentPage ? i : `<a href="#" data-gr-available-page="${i}">${i}</a>`}</span>`);
      } else if (i === 2 || i === totalPages - 1) {
        parts.push('<span class="page dots">…</span>');
      }
    }

    parts.push(`<span class="next"><a href="#" data-gr-available-page="${Math.min(currentPage + 1, totalPages)}" ${currentPage === totalPages ? 'aria-disabled="true"' : ''}>›<span class="visually-hidden">Next</span></a></span>`);
    container.innerHTML = `<div class="pagination pagination-type--paginated"><div class="row"><div class="small-12 columns"><div class="page-numbers nav-links">${parts.join('')}</div></div></div></div>`;

    container.querySelectorAll('[data-gr-available-page]').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        if (link.getAttribute('aria-disabled') === 'true') return;
        renderPage(activeGrid, items, parseInt(link.dataset.grAvailablePage, 10), true);
        activeGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  function renderPage(activeGrid, items, pageNumber, pushState = false) {
    const perPage = Math.max(parseInt(activeGrid.dataset.productsPerPage || '24', 10), 1);
    const paginationType = activeGrid.dataset.paginationType || 'paginated';
    const totalPages = Math.max(Math.ceil(items.length / perPage), 1);
    const page = Math.min(Math.max(parseInt(pageNumber || '1', 10), 1), totalPages);
    const loadingOverlay = activeGrid.querySelector(':scope > .loading-overlay');
    const start = (page - 1) * perPage;
    const pageItems = (paginationType === 'loadmore' || paginationType === 'infinite')
      ? items.slice(0, page * perPage)
      : items.slice(start, start + perPage);

    isRendering = true;
    clearGrid(activeGrid);

    const fragment = document.createDocumentFragment();
    pageItems.forEach((entry) => fragment.appendChild(entry.item.cloneNode(true)));

    if (loadingOverlay) {
      activeGrid.insertBefore(fragment, loadingOverlay);
    } else {
      activeGrid.appendChild(fragment);
    }

    renderPagination(activeGrid, items, page, paginationType);

    if (pushState) {
      const url = new URL(window.location.href);
      if (page > 1) {
        url.searchParams.set('page', page);
      } else {
        url.searchParams.delete('page');
      }
      window.history.pushState({ grAvailablePricePage: page }, '', url.toString());
    }

    window.setTimeout(() => {
      isRendering = false;
    }, 0);
  }

  async function runAvailablePriceSort() {
    const activeGrid = grid();
    if (!activeGrid) return;

    if (!isPriceSort()) {
      cleanupPagination();
      return;
    }

    const runId = ++latestRun;
    const sectionId = activeGrid.dataset.id;
    const totalPages = Math.max(parseInt(activeGrid.dataset.totalPages || '1', 10), 1);
    const currentPage = Math.max(parseInt(params().get('page') || activeGrid.dataset.currentPage || '1', 10), 1);
    const seen = new Set();
    const allItems = [];

    activeGrid.classList.add('loading');

    try {
      let pageItems = [];
      if (totalPages > 1 && sectionId) {
        const fetchedPages = await Promise.all(Array.from({ length: totalPages }, (_, index) => fetchPageItems(index + 1, sectionId)));
        pageItems = fetchedPages.flat();
      } else {
        pageItems = itemsFromGrid(activeGrid);
      }

      if (runId !== latestRun) return;

      pageItems.forEach((entry, index) => {
        if (seen.has(entry.key)) return;
        seen.add(entry.key);
        allItems.push({ ...entry, index });
      });

      renderPage(activeGrid, sortedItems(allItems), currentPage);
    } catch (error) {
      console.warn('Gear Rack available-price sort could not finish:', error);
    } finally {
      activeGrid.classList.remove('loading');
    }
  }

  function observeFacetRenders() {
    const target = document.getElementById('ProductGridContainer');
    if (!target || observer) return;

    observer = new MutationObserver(() => {
      if (isRendering) return;
      window.clearTimeout(observeFacetRenders.timeout);
      observeFacetRenders.timeout = window.setTimeout(runAvailablePriceSort, 125);
    });

    observer.observe(target, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      observeFacetRenders();
      runAvailablePriceSort();
    });
  } else {
    observeFacetRenders();
    runAvailablePriceSort();
  }

  window.addEventListener('popstate', () => {
    window.setTimeout(runAvailablePriceSort, 50);
  });
})();
